import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as UserIcon,
  Factory as PlantIcon,
  Settings as SettingsIcon,
  Build as SparesIcon,
  Gavel as RuleIcon,
  CheckCircleOutline as ApprovalIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import PrimaryLogo from '../../assets/images/icons/downtime.png'
import factory from '../../assets/images/navbar/factory.svg'


interface ModuleData {
  ID: number;
  ROLEID: number;
  MODULEID: number;
  CREATEPERM: boolean;
  READPERM: boolean;
  UPDATEPERM: boolean;
  DELETEPERM: boolean;
  STATUS: boolean;
  createdAt: string;
  updatedAt: string;
  moduleInfo: {
    ID: number;
    OUID: number;
    MODULENAME: string;
    MODULEDESC: string;
    ROUTE: string;
    ACTIVE: boolean;
    REVEAL: boolean;
    SEQUENCE: number;
    createdAt: string;
    updatedAt: string;
  };
}

interface SidebarItemProps {
  module: ModuleData;
  onItemClick?: () => void;
  sidebarOpen: boolean;
}

// Default icon mapping
const getModuleIcon = (moduleName: string) => {
  const iconMap: { [key: string]: any } = {
    DASHBOARD: DashboardIcon,
    USER_MANAGEMENT: UserIcon,
    PLANT_MANAGEMENT: PlantIcon,
    RULE_MANAGEMENT: RuleIcon,
    APPROVAL_MANAGEMENT: ApprovalIcon,
    SETTINGS: SettingsIcon,
    SPARES: SparesIcon,
    PROCESS_ORDER: () => <img src={factory} alt="Factory" />,
  };

  return iconMap[moduleName] || DashboardIcon;
};

const SidebarItem: React.FC<SidebarItemProps> = ({
  module,
  onItemClick,
  sidebarOpen
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = location.pathname.includes(`/${module.moduleInfo.ROUTE}`);
  const IconComponent = getModuleIcon(module.moduleInfo.MODULENAME);

  const handleClick = () => {
    navigate(`/${module.moduleInfo.ROUTE}`);
    onItemClick?.();
  };

  if (!module.moduleInfo.REVEAL) {
    return null;
  }

  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <ListItemButton
        onClick={handleClick}
        sx={{
          minHeight: 48,
          justifyContent: sidebarOpen ? 'initial' : 'center',
          px: 2.5,
          mx: 1,
          my: 0.5,
          borderRadius: 2,
          backgroundColor: isActive ? '#002060' : 'transparent',
          color: isActive ? 'primary.contrastText' : 'text.primary',
          '&:hover': {
            backgroundColor: isActive ? '#1d3971' : 'action.hover',
          },
          transition: (theme) => theme.transitions.create(['background-color', 'color'], {
            duration: theme.transitions.duration.shortest,
          }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: sidebarOpen ? 3 : 'auto',
            justifyContent: 'center',
            color: 'inherit',
          }}
        >
          <IconComponent />
        </ListItemIcon>
        <ListItemText
          primary={module.moduleInfo.MODULEDESC}
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: isActive ? 600 : 400,
            noWrap: true,
          }}
          sx={{
            opacity: sidebarOpen ? 1 : 0,
            transition: (theme) => theme.transitions.create('opacity'),
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  isMobile: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle, isMobile }) => {
  const modulesData = useSelector((data: any) => data.auth.moduleDetails?.moduleDetails || []);

  // Filter and sort modules
  const visibleModules = modulesData
    .filter((module: ModuleData) => module.moduleInfo.REVEAL)
    .sort((a: ModuleData, b: ModuleData) => a.moduleInfo.SEQUENCE - b.moduleInfo.SEQUENCE);

  const handleItemClick = () => {
    if (isMobile) {
      onToggle();
    }
  };

  const drawerContent = (
    <Box sx={{ overflow: 'auto' }}>
      {/* Sidebar Header */}
      {open && (

        <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img src={PrimaryLogo} alt="Logo" style={{ width: '70%', height: '70%' }} />
          </Box>

          {/* App title */}
          <Typography
            variant="h6"
            noWrap={false}
            component="div"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              fontSize: 16,
              whiteSpace: 'pre-line', 
            }}
          >
            DownTime{'\n'}MANAGEMENT
          </Typography>
        </Toolbar>
      )}

      {/* Navigation Items */}
      <List sx={{ px: 1 }}>
        {visibleModules.length > 0 ? (
          visibleModules.map((module: ModuleData) => (
            <SidebarItem
              key={module.moduleInfo.ID}
              module={module}
              onItemClick={handleItemClick}
              sidebarOpen={open}
            />
          ))
        ) : (
          <ListItem>
            <ListItemText
              primary="No modules available"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                color: 'text.secondary',
                fontStyle: 'italic',
                textAlign: 'center',
              }}
            />
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? open : true}
      onClose={onToggle}
      sx={{
        width: open ? 240 : 72,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? 240 : 72,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          overflowX: 'hidden',
          transition: (theme) => theme.transitions.create('width', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
        },
      }}
      ModalProps={{
        keepMounted: true, // Better mobile performance
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;